import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { AnnotationMetadata } from '@/features/annotation'
import {
  BUBBLE_OFFSET_LEFT,
  BUBBLE_OFFSET_TOP,
} from '@/features/annotation/constants.ts'
import type { HighlightStyle } from '@/features/annotation/core/PdfAnnotationEngine'
import { PdfAnnotationEngine } from '@/features/annotation/core/PdfAnnotationEngine'
import {
  EAnnotationEngine,
  PDFAnchorFragment,
  PdfPageProxyLike,
} from '@/features/annotation/types'
import { asPdfAnnotation } from '@/features/annotation-management'
import { useDocumentStore } from '@/features/document'
import { ResponseThread } from '@/schema'

import { useGetThreadsList } from '../../annotation-management'
import { useCommentStore, useTflViewerStore } from '../store'

import { useClickDetection } from './useClickDetection'
import { useCommentAvatars } from './useCommentAvatars'
import { useCommentDialog } from './useCommentDialog'

const PERSISTED_STYLE: HighlightStyle = {
  color: '#ffeb3b',
  alpha: 0.4,
}

export type PageRefs = {
  wrapper: HTMLDivElement | null
  highlightCanvas: HTMLCanvasElement | null
  /** Dedicated canvas for search focus highlight (focusSearchResult); not used by annotation engine */
  focusHighlightCanvas: HTMLCanvasElement | null
  controlCanvas: HTMLCanvasElement | null
  bubbleLayer: HTMLDivElement | null // use as a base anchoring for bubbles
}

interface UsePdfViewerControllerParams {
  documentId: string
  pdfContainerRef: React.RefObject<HTMLDivElement>
  numPages?: number | null
}

export interface UsePdfViewerControllerResult {
  containerRef: React.RefObject<HTMLDivElement>
  pdfEngineRef: React.RefObject<PdfAnnotationEngine | null>
  isPdfEngineReady: boolean
  pageRegistryVersion: number
  pageRefsMap: React.RefObject<Map<number, PageRefs>>
  handlePageLoadSuccess: (
    page: PdfPageProxyLike,
    pageIndex: number
  ) => Promise<void>
  handlePointerDown: (event: PointerEvent, pageIndex: number) => void
  handlePointerMove: (event: PointerEvent) => void
  handlePointerUp: (event: PointerEvent) => void
  handlePointerCancel: () => void
  pendingBubble: {
    show: boolean
    position: { top: number; left: number } | null
    onAddComment: () => void
  }
  dialogState: {
    open: boolean
    documentVersionId: string | undefined
    annotation: AnnotationMetadata | null
    onOpenChange: (open: boolean) => void
    onSuccess: (commentId: string) => void
  }
  dialog: ReturnType<typeof useCommentDialog>
  avatars: Array<{
    commentId: string
    comment: ResponseThread
    metadata: AnnotationMetadata
    isActive: boolean
  }>
  showCommentAvatars: boolean
  onAvatarClick: (commentId: string) => void
  onClickOutside: (event: React.MouseEvent<HTMLDivElement>) => void
  onMouseDown: (event: React.MouseEvent<HTMLDivElement>) => void
  bubbleOffset: { top: number; left: number }
}

export const usePdfViewerController = ({
  documentId,
  pdfContainerRef,
  numPages: _numPages,
}: UsePdfViewerControllerParams): UsePdfViewerControllerResult => {
  const { metadataByDocId, setRightSidebarCollapsed } = useTflViewerStore()
  const { activeDocumentId, getIsLatestVersion } = useDocumentStore()
  const isLatestVersion = activeDocumentId
    ? getIsLatestVersion(activeDocumentId)
    : true
  const {
    setComments,
    setAnnotation,
    activeCommentId,
    setActiveCommentId,
    setActiveDocumentVersionId,
    commentsByDocVersion,
    annotationsByDocVersion,
    showCommentAvatarsByDoc,
    setShowCommentAvatars,
  } = useCommentStore()

  const showCommentAvatars = useMemo(
    () => showCommentAvatarsByDoc[documentId] ?? true,
    [documentId, showCommentAvatarsByDoc]
  )

  useEffect(() => {
    if (showCommentAvatarsByDoc[documentId] === undefined) {
      setShowCommentAvatars(documentId, true)
    }
  }, [documentId, showCommentAvatarsByDoc, setShowCommentAvatars])

  const containerRef = useRef<HTMLDivElement>(null)
  const pageRefsMap = useRef<Map<number, PageRefs>>(new Map())
  const pdfEngineRef = useRef<PdfAnnotationEngine | null>(null)
  const [isPdfEngineReady, setIsPdfEngineReady] = useState(false)
  const [pageRegistryVersion, setPageRegistryVersion] = useState(0)

  const documentVersionId = useMemo(
    () => metadataByDocId[documentId]?.version_id,
    [metadataByDocId, documentId]
  )
  const isActive = useMemo(
    () => activeDocumentId === documentId,
    [activeDocumentId, documentId]
  )

  const { data } = useGetThreadsList({
    documentVersionId: documentVersionId ?? '',
  })

  const comments = useMemo(() => {
    return data?.pages?.flatMap((page) => page?.data) || null
  }, [data])

  const clearPdfSelection = useCallback(() => {
    pdfEngineRef.current?.clearSelection()
  }, [])

  const handleAnnotationAdded = useCallback(
    (commentId: string, metadata: AnnotationMetadata) => {
      setAnnotation(commentId, documentId, metadata)
      setActiveCommentId(commentId)
      pdfEngineRef.current?.clearSelection()
    },
    [setAnnotation, setActiveCommentId, documentId]
  )

  const dialog = useCommentDialog({
    documentVersionId,
    onSelectionCleared: clearPdfSelection,
    onCommentAdded: handleAnnotationAdded,
  })

  const avatarsHook = useCommentAvatars({
    documentVersionId,
    showCommentAvatars,
    activeCommentId,
    commentsByDocVersion,
    annotationsByDocVersion,
    isActive,
  })

  const clickDetection = useClickDetection()

  const bubblePosition = useMemo(() => {
    if (!dialog.pendingAnnotation.metadata || !pdfEngineRef.current) return null

    if (dialog.pendingAnnotation.metadata.engine !== EAnnotationEngine.PDF) {
      return null
    }

    const firstFragment = dialog.pendingAnnotation.metadata.fragments?.[0]
    if (!firstFragment) return null

    const anchorRect = pdfEngineRef.current.getAnchorPageRect(
      firstFragment as PDFAnchorFragment
    )
    if (!anchorRect) return null

    return {
      top: anchorRect.top + BUBBLE_OFFSET_TOP,
      left: anchorRect.left + BUBBLE_OFFSET_LEFT,
    }
  }, [dialog.pendingAnnotation.metadata])

  const bubbleOffset = useMemo(
    () => ({ top: BUBBLE_OFFSET_TOP, left: BUBBLE_OFFSET_LEFT }),
    []
  )

  const handlePdfSelectionCommitted = useCallback(
    (metadata: AnnotationMetadata | null) => {
      if (!metadata || !pdfContainerRef.current) {
        dialog.clearPending()
        return
      }

      if (metadata.fragments.length > 0) {
        dialog.setPendingBubble(metadata, true)
      }
    },
    [pdfContainerRef, dialog]
  )
  const onSelectionCommittedRef = useRef<
    ((m: AnnotationMetadata | null) => void) | null
  >(null)

  useEffect(() => {
    onSelectionCommittedRef.current = handlePdfSelectionCommitted
  }, [handlePdfSelectionCommitted])

  const handleAvatarClick = useCallback(
    (commentId: string) => {
      setActiveCommentId(commentId)
      setRightSidebarCollapsed(false)

      if (!pdfEngineRef.current || !documentVersionId) return

      const pdf = asPdfAnnotation(
        annotationsByDocVersion[documentVersionId]?.[commentId]
      )
      if (!pdf) return

      pdfEngineRef.current.apply(pdf, commentId, PERSISTED_STYLE)
    },
    [
      setActiveCommentId,
      setRightSidebarCollapsed,
      pdfEngineRef,
      documentVersionId,
      annotationsByDocVersion,
    ]
  )

  const handlePageLoadSuccess = useCallback(
    async (page: PdfPageProxyLike, pageIndex: number): Promise<void> => {
      // Always register pages so search focus highlight can draw on any version.
      // isLatestVersion is still used for selection/comment bubble (pendingBubble.show).
      const engine = pdfEngineRef.current
      if (!engine) return

      const pageRefs = pageRefsMap.current.get(pageIndex)
      if (
        !pageRefs?.wrapper ||
        !pageRefs?.highlightCanvas ||
        !pageRefs?.controlCanvas ||
        !pageRefs?.bubbleLayer
      ) {
        return
      }

      const pageWidth = 800
      const scale = pageWidth / page.getViewport({ scale: 1 }).width
      const viewport = page.getViewport({ scale })

      pageRefs.highlightCanvas.width = viewport.width
      pageRefs.highlightCanvas.height = viewport.height
      pageRefs.controlCanvas.width = viewport.width
      pageRefs.controlCanvas.height = viewport.height

      if (pageRefs.focusHighlightCanvas) {
        pageRefs.focusHighlightCanvas.width = viewport.width
        pageRefs.focusHighlightCanvas.height = viewport.height
      }

      await engine.registerPage(
        pageIndex,
        {
          wrapper: pageRefs.wrapper,
          highlightCanvas: pageRefs.highlightCanvas,
          controlCanvas: pageRefs.controlCanvas,
          bubbleLayer: pageRefs.bubbleLayer,
        },
        page,
        viewport,
        pageWidth,
        viewport.height
      )
      setPageRegistryVersion((v) => v + 1)
    },
    [pdfEngineRef]
  )

  const handlePointerDown = useCallback(
    (event: PointerEvent, pageIndex: number) => {
      if (!pdfEngineRef.current) return
      pdfEngineRef.current.handlePointerDown(event, pageIndex)
    },
    [pdfEngineRef]
  )

  const handlePointerMove = useCallback(
    (event: PointerEvent) => {
      if (!pdfEngineRef.current) return
      pdfEngineRef.current.handlePointerMove(event)
    },
    [pdfEngineRef]
  )

  const handlePointerUp = useCallback(
    (event: PointerEvent) => {
      if (!pdfEngineRef.current) return
      pdfEngineRef.current.handlePointerUp(event)
    },
    [pdfEngineRef]
  )

  const handlePointerCancel = useCallback(() => {
    if (!pdfEngineRef.current) return
    pdfEngineRef.current.handlePointerCancel()
  }, [pdfEngineRef])

  const handleClickOutside = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (dialog.dialogOpen) return

      const isClick = clickDetection.isActualClick(event)

      if (isClick) {
        setActiveCommentId(undefined)
        if (pdfEngineRef.current) {
          pdfEngineRef.current.remove()
        }
        if (!dialog.dialogOpen) {
          dialog.clearPending()
        }
      }
    },
    [setActiveCommentId, dialog, clickDetection, pdfEngineRef]
  )

  useEffect(() => {
    setActiveDocumentVersionId(documentVersionId)
  }, [documentVersionId, setActiveDocumentVersionId])

  useEffect(() => {
    if (!comments || !documentVersionId || !isActive) return

    setComments(documentVersionId, comments)

    comments.forEach((comment) => {
      if (comment.annotation) {
        setAnnotation(documentVersionId, comment.id, comment.annotation)
      }
    })
  }, [comments, setComments, setAnnotation, documentVersionId, isActive])

  useEffect(() => {
    if (!documentVersionId || !pdfEngineRef.current) return

    // Always clear first so we don't keep stale drawings.
    pdfEngineRef.current.remove()

    if (!activeCommentId) return

    const pdf = asPdfAnnotation(
      annotationsByDocVersion[documentVersionId]?.[activeCommentId]
    )
    if (pdf) {
      pdfEngineRef.current.apply(pdf, activeCommentId, PERSISTED_STYLE)
    }
  }, [activeCommentId, documentVersionId, annotationsByDocVersion])

  useEffect(() => {
    if (!documentVersionId) {
      pdfEngineRef.current?.unregisterAllPages()
      pdfEngineRef.current?.reset()
      pdfEngineRef.current = null
      setIsPdfEngineReady(false)
      setPageRegistryVersion(0)
      return
    }

    const engine = new PdfAnnotationEngine({
      onSelectionCommitted: (m) => onSelectionCommittedRef.current?.(m),
      persistedHighlightStyle: PERSISTED_STYLE,
    })

    pdfEngineRef.current = engine
    setIsPdfEngineReady(true)
    setPageRegistryVersion(0)

    return () => {
      engine.unregisterAllPages()
      engine.reset()
      if (pdfEngineRef.current === engine) pdfEngineRef.current = null
      setIsPdfEngineReady(false)
      setPageRegistryVersion(0)
    }
  }, [documentVersionId])

  return {
    containerRef,
    pdfEngineRef,
    isPdfEngineReady,
    pageRegistryVersion,
    pageRefsMap,
    handlePageLoadSuccess,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    handlePointerCancel,
    pendingBubble: {
      show:
        dialog.pendingAnnotation.showBubble &&
        !!bubblePosition &&
        isLatestVersion,
      position: bubblePosition,
      onAddComment: dialog.pendingBubble.onAddComment,
    },
    dialogState: dialog.dialogState,
    dialog,
    avatars: avatarsHook.avatars,
    showCommentAvatars,
    onAvatarClick: handleAvatarClick,
    onClickOutside: handleClickOutside,
    onMouseDown: clickDetection.handleMouseDown,
    bubbleOffset,
  }
}
